/**
 * Modal reusable para crear UNA factura desde MÚLTIPLES remitos en cuenta corriente.
 *
 * Soporta dos modos:
 *   - Cliente preseleccionado (clientId pasado por prop) → muestra directo los remitos
 *   - Sin cliente → muestra primero un combobox para seleccionar cliente
 *
 * Al confirmar:
 *   - POST /api/invoices/from-remitos { remitoIds }
 *   - Llama onSuccess con la factura creada
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DocumentTextIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ClientCombobox from './ClientCombobox';
import { BillingProfile, etiquetaCondicion, formatearDoc } from './BillingProfiles';

interface UnbilledRemito {
  id: string;
  remitNumber: string;
  createdAt: string;
  issuedAt: string;
  total: number;
  itemCount: number;
  itemsSummary: string;
}

interface MultiRemitoInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialClientId?: string;          // si viene, no se muestra selector de cliente
  initialClientName?: string;        // para mostrar header
  preselectedRemitoIds?: string[];   // si viene, se preseleccionan estos checkboxes
  onSuccess?: (invoice: any) => void;
}

const formatARS = (n: number) => new Intl.NumberFormat('es-AR', {
  style: 'currency', currency: 'ARS', minimumFractionDigits: 2
}).format(n);

// Los métodos salen de la configuración (Ajustes → Métodos de pago), que es la
// fuente única: tenerlos escritos acá dejaba afuera cuenta corriente y cheque, y
// los recargos quedaban desfasados de los reales.
const FALLBACK_PAYMENT_OPTIONS = [
  { value: 'cuenta_corriente', label: 'Cuenta Corriente', surcharge: 0 },
  { value: 'efectivo',         label: 'Efectivo',         surcharge: 0 },
  { value: 'transferencia',    label: 'Transferencia',    surcharge: 0 },
];

const MultiRemitoInvoiceDialog: React.FC<MultiRemitoInvoiceDialogProps> = ({
  open, onOpenChange, initialClientId, initialClientName, preselectedRemitoIds = [], onSuccess
}) => {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(initialClientId || '');
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [remitos, setRemitos] = useState<UnbilledRemito[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(preselectedRemitoIds));
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('cuenta_corriente');
  const [paymentOptions, setPaymentOptions] = useState(FALLBACK_PAYMENT_OPTIONS);
  useEffect(() => {
    axios.get('/api/settings/payment-methods')
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data.filter((m: any) => m.enabled !== 0) : [];
        if (!rows.length) return;
        setPaymentOptions(rows.map((m: any) => ({
          value: m.code,
          label: m.name,
          // el % negativo (pago anticipado) hoy no se aplica en ningún lado
          surcharge: Number(m.surcharge) > 0 ? Number(m.surcharge) / 100 : 0
        })));
      })
      .catch(() => { /* queda el fallback */ });
  }, []);

  // Cargar lista de clientes (sólo si NO hay cliente preseleccionado)
  useEffect(() => {
    if (!open || initialClientId) return;
    axios.get('/api/clients?limit=10000')
      .then((res) => setClients(res.data.clients || []))
      .catch(() => setClients([]));
  }, [open, initialClientId]);

  // Reset state al abrir/cerrar
  useEffect(() => {
    if (open) {
      setSelectedClientId(initialClientId || '');
      setSelectedIds(new Set(preselectedRemitoIds));
    }
  }, [open, initialClientId, JSON.stringify(preselectedRemitoIds)]);

  // Setear selectedClient cuando cambia el id
  useEffect(() => {
    if (!selectedClientId) { setSelectedClient(null); return; }
    if (initialClientId && initialClientName) {
      setSelectedClient({ id: initialClientId, companyName: initialClientName });
      return;
    }
    const found = clients.find((c) => c.id === selectedClientId);
    setSelectedClient(found || null);
  }, [selectedClientId, clients, initialClientId, initialClientName]);

  // A nombre de quién sale la factura. Si el cliente tiene datos de
  // facturación alternativos se puede elegir; si no, salen los de su ficha.
  const [perfiles, setPerfiles] = useState<BillingProfile[]>([]);
  const [perfilId, setPerfilId] = useState<string>('');

  useEffect(() => {
    if (!selectedClientId || !open) { setPerfiles([]); setPerfilId(''); return; }
    axios.get(`/api/clients/${selectedClientId}/billing-profiles`)
      .then((res) => {
        const lista: BillingProfile[] = res.data.profiles || [];
        setPerfiles(lista);
        setPerfilId(lista.find((p) => Number(p.isDefault) === 1)?.id || '');
      })
      .catch(() => { setPerfiles([]); setPerfilId(''); });
  }, [selectedClientId, open]);

  // Cargar remitos sin facturar cuando hay cliente seleccionado
  useEffect(() => {
    if (!selectedClientId || !open) {
      setRemitos([]);
      return;
    }
    setLoading(true);
    axios.get(`/api/invoices/unbilled-remitos?clientId=${selectedClientId}`)
      .then((res) => setRemitos(res.data.remitos || []))
      .catch((err) => {
        console.error('Error cargando remitos:', err);
        toast.error('Error al cargar remitos');
        setRemitos([]);
      })
      .finally(() => setLoading(false));
  }, [selectedClientId, open]);

  const subtotal = useMemo(() => {
    return remitos
      .filter((r) => selectedIds.has(r.id))
      .reduce((sum, r) => sum + (r.total || 0), 0);
  }, [remitos, selectedIds]);

  const selectedPaymentOption = paymentOptions.find((o) => o.value === paymentMethod);
  const surchargeRate = selectedPaymentOption?.surcharge ?? 0;
  const surchargeAmount = subtotal * surchargeRate;
  const total = subtotal + surchargeAmount;

  const toggleAll = () => {
    if (selectedIds.size === remitos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(remitos.map((r) => r.id)));
    }
  };

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleCreate = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleccioná al menos un remito');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post('/api/invoices/from-remitos', {
        remitoIds: Array.from(selectedIds),
        paymentMethod,
        surchargeRate,
        surchargeAmount: Math.round(surchargeAmount * 100) / 100,
        billingProfileId: perfilId || null,
      });
      toast.success(`Factura ${res.data.invoiceNumber} creada en borrador`);
      onSuccess?.(res.data);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al crear la factura');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
            Facturar remitos en cuenta corriente
          </DialogTitle>
          <DialogDescription>
            Genera una sola factura agrupando varios remitos pendientes en cta cte del mismo cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Selector de cliente (sólo si no vino preseleccionado) */}
          {!initialClientId && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Cliente</label>
              <ClientCombobox
                clients={clients}
                selectedClient={selectedClient}
                onSelect={(c) => setSelectedClientId(c?.id || '')}
                placeholder="Buscar cliente..."
                getClientName={(c) => c.companyName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Sin nombre'}
              />
            </div>
          )}

          {initialClientId && initialClientName && (
            <div className="text-sm">
              <span className="text-muted-foreground">Cliente: </span>
              <span className="font-medium">{initialClientName}</span>
            </div>
          )}

          {/* Lista de remitos */}
          {selectedClientId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Remitos pendientes en cta cte ({remitos.length})
                </span>
                {remitos.length > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={toggleAll} className="text-xs">
                    {selectedIds.size === remitos.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8 text-sm text-muted-foreground">Cargando remitos...</div>
              ) : remitos.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                  Este cliente no tiene remitos pendientes en cuenta corriente
                </div>
              ) : (
                <div className="border border-border rounded-lg divide-y divide-border max-h-[40vh] overflow-y-auto">
                  {remitos.map((r) => {
                    const isSelected = selectedIds.has(r.id);
                    return (
                      <label
                        key={r.id}
                        className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-cyan-50 dark:bg-cyan-900/20' : 'hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(r.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-cyan-700 dark:text-cyan-400">
                              {r.remitNumber}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {r.itemCount} {r.itemCount === 1 ? 'item' : 'items'}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(r.createdAt).toLocaleDateString('es-AR')}
                            </span>
                          </div>
                          {r.itemsSummary && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              {r.itemsSummary}
                            </div>
                          )}
                        </div>
                        <div className="text-sm font-semibold whitespace-nowrap">
                          {formatARS(r.total)}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedIds.size > 0 && (
                <div className="space-y-3">
                  {/* A nombre de quién sale: solo aparece si el cliente tiene
                      datos de facturación alternativos cargados */}
                  {perfiles.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Facturar a</label>
                      <select
                        value={perfilId}
                        onChange={(e) => setPerfilId(e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">
                          {selectedClient?.name || 'Datos del cliente'} (ficha)
                        </option>
                        {perfiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre || '(sin razón social)'} — {p.docType} {formatearDoc(p.docNumber, p.docType)} · {etiquetaCondicion(p.ivaCondition)}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        La condición de IVA del destinatario define si sale factura A o B.
                      </p>
                    </div>
                  )}

                  {/* Método de pago */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Método de pago</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {paymentOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}{o.surcharge > 0 ? ` (+${(o.surcharge * 100).toFixed(0)}%)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Resumen de totales */}
                  <div className="rounded-lg border border-cyan-300 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-900/20 divide-y divide-cyan-200 dark:divide-cyan-800">
                    <div className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-muted-foreground">
                        Subtotal ({selectedIds.size} remito{selectedIds.size === 1 ? '' : 's'})
                      </span>
                      <span className="tabular-nums">{formatARS(subtotal)}</span>
                    </div>
                    {surchargeRate > 0 && (
                      <div className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-amber-600 dark:text-amber-400">
                          Recargo {selectedPaymentOption?.label} ({(surchargeRate * 100).toFixed(0)}%)
                        </span>
                        <span className="text-amber-600 dark:text-amber-400 tabular-nums font-medium">
                          + {formatARS(surchargeAmount)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-sm font-semibold">Total</span>
                      <span className="text-lg font-bold text-cyan-700 dark:text-cyan-400 tabular-nums">
                        {formatARS(total)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={creating || selectedIds.size === 0}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <CheckIcon className="h-4 w-4 mr-1" />
            {creating ? 'Creando...' : `Crear factura (${selectedIds.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MultiRemitoInvoiceDialog;
